import { useState, useRef, useCallback, useEffect } from 'react';

const useSpeechRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const shouldRestartRef = useRef(false);
  const callActiveRef = useRef(false);
  const restartTimerRef = useRef(null);
  const isSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

  const setCallActive = useCallback((active) => {
    callActiveRef.current = active;
    if (!active) {
      shouldRestartRef.current = false;
      if (restartTimerRef.current) {
        clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
    }
  }, []);

  const cleanup = useCallback(() => {
    shouldRestartRef.current = false;
    callActiveRef.current = false;
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (e) {}
      try { recognitionRef.current.stop(); } catch (e) {}
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onend = null;
      recognitionRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isSupported) {
      setError('not-supported');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let finalText = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += (finalText ? ' ' : '') + result[0].transcript;
        } else {
          interim += (interim ? ' ' : '') + result[0].transcript;
        }
      }
      if (finalText) {
        setTranscript((prev) => (prev ? prev + ' ' : '') + finalText);
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event) => {
      if (!callActiveRef.current) return;
      if (event.error === 'not-allowed') {
        setError('not-allowed');
        shouldRestartRef.current = false;
        setIsListening(false);
        return;
      }
      setIsListening(false);
      if (shouldRestartRef.current && callActiveRef.current) {
        restartTimerRef.current = setTimeout(() => {
          if (shouldRestartRef.current && callActiveRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
              setIsListening(true);
            } catch (e) {}
          }
        }, 250);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      if (shouldRestartRef.current && callActiveRef.current) {
        restartTimerRef.current = setTimeout(() => {
          if (shouldRestartRef.current && callActiveRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
              setIsListening(true);
            } catch (e) {}
          }
        }, 100);
      }
    };

    recognitionRef.current = recognition;

    return cleanup;
  }, [isSupported, cleanup]);

  const startListening = useCallback((lang = 'en-US') => {
    if (!recognitionRef.current) return;
    setError(null);
    shouldRestartRef.current = true;
    callActiveRef.current = true;
    try {
      recognitionRef.current.lang = lang;
      recognitionRef.current.start();
      setIsListening(true);
    } catch (err) {
      if (err.message && err.message.includes('already started')) {
        setIsListening(true);
      }
    }
  }, []);

  const stopListening = useCallback(() => {
    shouldRestartRef.current = false;
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
      setIsListening(false);
    } catch (err) {}
  }, []);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
  }, []);

  return {
    isListening,
    transcript,
    interimTranscript,
    error,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
    setCallActive,
  };
};

export default useSpeechRecognition;
